import { useMemo, useSyncExternalStore } from "react";
import type * as Y from "yjs";
import type { FormContent } from "../types";
import { FormDraft, readForm } from "../collab/doc";

/**
 * Subscribe a component to a collaborative draft.
 *
 * Re-reads the whole document on every change rather than diffing. A form is
 * small — tens of questions — and reading it whole means the UI can never show
 * a partially applied remote update, which is the failure mode that makes
 * collaborative editing feel broken.
 *
 * The read happens in the same tick as the change. That's what keeps the
 * caret where it is: the builder's fields are controlled, so React sets the
 * field's value to whatever this hook hands back. When the re-read was
 * deferred to the next animation frame, a keystroke was applied to the
 * document, then rendered against the old content — reverting the field —
 * and a frame later rendered against the new content, overwriting the field
 * and pushing the caret to the end. Anyone typing faster than a frame lost
 * letters, and everyone lost their place. Reading synchronously means the
 * value React renders already equals what's in the field, so React leaves the
 * field alone.
 *
 * `useSyncExternalStore` also settles which document `content` describes:
 * the store is minted per document, so a render can never pair one form's
 * content with another's draft.
 */
export function useFormDraft(doc: Y.Doc | null): {
  content: FormContent | null;
  draft: FormDraft | null;
} {
  const store = useMemo(() => (doc ? createDraftStore(doc) : null), [doc]);
  const content = useSyncExternalStore(
    store ? store.subscribe : noSubscribe,
    store ? store.getSnapshot : noSnapshot,
  );
  return { content, draft: store?.draft ?? null };
}

const noSubscribe = () => () => {};
const noSnapshot = () => null;

/**
 * The document as an external store. Yjs emits `update` synchronously at the
 * end of every transaction, local or remote; the snapshot is re-read lazily
 * on the next request after one, and reused until then, so React sees a
 * stable reference while nothing has changed.
 */
function createDraftStore(doc: Y.Doc): {
  draft: FormDraft;
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => FormContent;
} {
  let content = readForm(doc);
  let stale = false;
  // Marked on the document itself, not only inside `subscribe`, so a change
  // between a render and its subscription is still seen when React re-checks
  // the snapshot after subscribing. Lives as long as the document does.
  doc.on("update", () => {
    stale = true;
  });
  return {
    draft: new FormDraft(doc),
    subscribe(onChange) {
      doc.on("update", onChange);
      return () => doc.off("update", onChange);
    },
    getSnapshot() {
      if (stale) {
        content = readForm(doc);
        stale = false;
      }
      return content;
    },
  };
}
